import { isClarityEligible, loadClarity, getClarityProjectId } from './clarity.js'
import { isPasswordRecoveryRoute } from './passwordRecoveryRoutes.js'
import { validPlanUuid } from './planNavigation.js'
import { trackGoogleAnalyticsEvent } from './consentedGoogleAnalytics.js'

/**
 * Camada de analytics da plataforma. Mantém a API histórica (trackEvent, trackButtonClick,
 * trackCtaImpression, trackCtaClick, trackSessionStart/End, bootstrapAnalytics) para que os
 * call sites não mudem; o backend é o Microsoft Clarity.
 *
 * Toda emissão é gated: só dispara quando há consentimento de analytics E o usuário é adulto
 * logado (ver isClarityEligible). Sem isso, tudo vira no-op — respeitando LGPD + ECA Digital.
 */

const canUseWindow = typeof window !== 'undefined'
const CTA_IMPRESSION_CACHE_LIMIT = 500
const ctaImpressionCache = new Set()

let context = { consent: null, user: null, now: null }
let sessionStarted = false
let sessionEnded = false
let lastIdentified = ''

export const setAnalyticsContext = (next = {}) => {
  context = {
    consent: next.consent ?? null,
    user: next.user ?? null,
    now: next.now ?? null,
    debug: next.debug,
  }
}

const isEligible = () =>
  !(canUseWindow && (window.__visualgSensitiveAuthPage || isPasswordRecoveryRoute(window.location.pathname))) && isClarityEligible({
    consent: context.consent,
    user: context.user,
    now: context.now || new Date(),
    debug: context.debug,
  })

const getCommonTags = () => {
  if (!canUseWindow) return {}
  const viewportWidth = Number(window.innerWidth)
  return {
    route: window.location?.pathname || '/',
    is_mobile: Number.isFinite(viewportWidth) ? String(viewportWidth < 768) : 'false',
  }
}

/** Dispara um evento custom no Clarity com tags. No-op se não elegível ou Clarity indisponível. */
const dispatch = (eventName, tags = {}) => {
  if (!eventName) return false
  if (!canUseWindow) return false
  if (!isEligible()) return false
  if (!loadClarity()) return false
  if (typeof window.clarity !== 'function') return false

  const userUuid = validPlanUuid(context.user?.profile_uuid || context.user?.uuid)
  const identityKey = `${userUuid || ''}:${window.location.pathname}`
  if (userUuid && lastIdentified !== identityKey) {
    window.clarity('identify', userUuid)
    lastIdentified = identityKey
  }

  const allTags = { ...getCommonTags(), ...tags }
  for (const [key, value] of Object.entries(allTags)) {
    if (value === undefined || value === null) continue
    window.clarity('set', key, String(value))
  }
  window.clarity('event', eventName)
  return true
}

export const trackEvent = (eventName, properties = {}) => {
  trackGoogleAnalyticsEvent(eventName, properties)
  dispatch(eventName, properties)
}

export const trackButtonClick = (buttonName, properties = {}) => {
  if (!buttonName) return
  dispatch('button_clicked', { button_name: buttonName, ...properties })
}

const registerCtaImpressionOnce = (cacheKey) => {
  if (!cacheKey) return true
  if (ctaImpressionCache.has(cacheKey)) return false
  if (ctaImpressionCache.size >= CTA_IMPRESSION_CACHE_LIMIT) ctaImpressionCache.clear()
  ctaImpressionCache.add(cacheKey)
  return true
}

export const trackCtaImpression = (ctaId, properties = {}, options = {}) => {
  const normalizedId = String(ctaId || '').trim()
  if (!normalizedId) return

  const shouldDedupe = options?.dedupe !== false
  if (shouldDedupe) {
    const fallbackRoute = canUseWindow ? window.location?.pathname || '/' : '/'
    const route = String(properties?.route || fallbackRoute).trim()
    const location = String(properties?.cta_location || '').trim()
    const variant = String(properties?.experiment_variant || '').trim()
    const cacheKey = `${normalizedId}::${route}::${location}::${variant}`
    if (!registerCtaImpressionOnce(cacheKey)) return
  }

  dispatch('cta_impression', { cta_id: normalizedId, ...properties })
}

export const trackCtaClick = (ctaId, properties = {}) => {
  const normalizedId = String(ctaId || '').trim()
  if (!normalizedId) return
  dispatch('cta_clicked', { cta_id: normalizedId, ...properties })
}

export const trackSessionStart = (properties = {}) => {
  if (sessionStarted) return
  if (!dispatch('session_started', properties)) return
  sessionStarted = true
  sessionEnded = false
}

export const trackSessionEnd = (reason = 'manual') => {
  if (!sessionStarted || sessionEnded) return
  if (!dispatch('session_ended', { end_reason: reason })) return
  sessionEnded = true
}

let listenersAttached = false
const attachSessionListeners = () => {
  if (!canUseWindow || listenersAttached) return
  listenersAttached = true
  window.addEventListener('pagehide', () => trackSessionEnd('pagehide'))
  window.addEventListener('beforeunload', () => trackSessionEnd('beforeunload'))
}

/** Inicializa o Clarity quando elegível e começa a sessão. Chamado pelo provider de consentimento. */
export const bootstrapAnalytics = () => {
  if (!canUseWindow) return false
  if (!getClarityProjectId()) return false
  if (!isEligible()) return false
  if (!loadClarity()) return false
  trackSessionStart()
  attachSessionListeners()
  return true
}
